import { ONLY, SELECT_ALL } from '@web-platform/shared-universal-constants/src/airlinesFilter';
import { MULTIPLE_KEY } from '@web-platform/shared-universal-constants/src/carrierTypes';
import {
  MUTATION_RESPONSE_RECEIVED,
  QUERY_RESPONSE_RECEIVED,
} from '@web-platform/shared-universal-constants/src/network';
import * as persistanceKeys from '@web-platform/shared-universal-constants/src/persistanceKeys';
import { ResultStatus } from '@web-platform/shared-universal-constants/src/radar';
import { SEARCH_PARAMS_CHANGED } from '@web-platform/shared-universal-constants/src/userSearch';
import {
  RADAR_CREATE_SESSIONS,
  RADAR_POLL,
} from '@web-platform/shared-universal-types/src/network/requestTypes';
import {
  getFlightsResponseBody,
  isResponseContainFlightsBody,
  isFlightsResponseError,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/radar/flightsResponse';
import { tryGetValue } from '@web-platform/shared-universal-utils-frontend/src/helpers/sessionStorage';
import mapValues from 'lodash/mapValues';

import {
  CARRIER_CONTROL_FILTER_CHANGED,
  CARRIERS_FILTER_CHANGED,
  FILTERS_RESET,
} from '../../../../../../actions/filters';
import { mapCarriers } from '../../../entities/flights/carriers/carriersMapper';
import { getCarriersFromUrl } from '../urlInitializers';

import {
  inferDefaultCarrierValue,
  inferDefaultCarrierValueResolvable,
  reconcileCarriersFilterState,
} from './helpers';
import { INITIAL_STATE } from './initialState';
import { coerceLegacyCarriersFilterState } from './legacyCarriersStateMigration';
import { NONE_INTENT_KEY } from './noneIntent';

import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { CarriersFilterState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/carriers';
import type { FiltersState } from '@web-platform/shared-universal-types/src/redux/state/flightSearchResults/features/filters/state';

export { INITIAL_STATE } from './initialState';

const filtersInSession = (): FiltersState =>
  // @ts-expect-error FiltersState type is not compatible with empty object
  tryGetValue(persistanceKeys.FILTERS, INITIAL_STATE);

const sessionFilters = (): CarriersFilterState =>
  coerceLegacyCarriersFilterState(filtersInSession()?.carriers);

const storedStateFilters = (): CarriersFilterState => {
  const fromUrl = getCarriersFromUrl();
  const fromSession = sessionFilters();
  if (
    NONE_INTENT_KEY in fromUrl &&
    Object.keys(fromSession).length > 0 &&
    !(NONE_INTENT_KEY in fromSession)
  ) {
    return fromSession;
  }
  return Object.keys(fromUrl).length > 0 ? fromUrl : fromSession;
};

export const createCarriersReducer = (
  getInitialState: () => CarriersFilterState = storedStateFilters,
  { multipleFollowsInferDefault = false }: { multipleFollowsInferDefault?: boolean } = {},
) => {
  // Snapshot `inferDefault` at the earliest moment of each polling cycle —
  // either when SEARCH_PARAMS_CHANGED fires for a new search (before we
  // soften explicit positives to `inferred`) or when the first radar response
  // of the cycle arrives. Both call sites freeze only if not already frozen,
  // so the explicit-positive truth survives subsequent softening and user
  // interactions. Unfrozen when Radar reports status COMPLETE.
  //
  // `hasUserDispatchedFilter` distinguishes URL-hydrated intent (sparse
  // explicit positives mean "only these") from user-applied intent (explicit
  // positives mean "I picked these from a known set, default newcomers in").
  // Once any user filter dispatch lands, the freeze flips to `true` so newly-
  // discovered carriers default to selected.
  let frozenInferDefault: boolean | null = null;
  let hasProcessedRadarResponse = false;
  let hasUserDispatchedFilter = false;

  return (state: CarriersFilterState = getInitialState(), action: Action): CarriersFilterState => {
    switch (action.type) {
      case CARRIERS_FILTER_CHANGED: {
        hasUserDispatchedFilter = true;
        const { aliases = [], snapshot, token, value } = action.payload;
        // When the user toggles a carrier while an alliance is active, the
        // dispatch site supplies a snapshot of the carriers currently visible
        // as selected. We materialise it as the new baseline so the
        // alliance-implied selections survive the alliance being cleared by
        // this same action (see alliances reducer).
        const next: CarriersFilterState = snapshot
          ? Object.fromEntries(snapshot.map((entry) => [entry.token, { value: entry.value }]))
          : { ...state };
        delete next[NONE_INTENT_KEY];
        aliases.forEach((alias) => {
          if (alias !== token) delete next[alias];
        });
        next[token] = { value };
        return next;
      }

      case FILTERS_RESET: {
        hasUserDispatchedFilter = true;
        const { [NONE_INTENT_KEY]: _sentinel, ...rest } = state;
        return mapValues(rest, () => ({ value: true }));
      }

      case CARRIER_CONTROL_FILTER_CHANGED: {
        hasUserDispatchedFilter = true;
        const next: CarriersFilterState = {};
        if (action.payload.action === ONLY) {
          const { onlyToken } = action.payload;
          action.payload.tokens.forEach((token) => {
            next[token] = { value: token === onlyToken };
          });
          return next;
        }
        const value = action.payload.action === SELECT_ALL;
        action.payload.tokens.forEach((token) => {
          next[token] = { value };
        });
        return next;
      }

      case SEARCH_PARAMS_CHANGED: {
        if (!hasProcessedRadarResponse) return state;
        const hasExplicitPositive = Object.values(state).some(
          (entry) => entry.inferred !== true && entry.value,
        );
        if (!hasExplicitPositive) return state;
        if (frozenInferDefault === null) {
          frozenInferDefault = hasUserDispatchedFilter ? true : inferDefaultCarrierValue(state);
        }
        return Object.fromEntries(
          Object.entries(state).map(([key, entry]) => {
            if (key === NONE_INTENT_KEY) return [key, entry];
            return [key, entry.value ? { value: true, inferred: true } : entry];
          }),
        );
      }

      case MUTATION_RESPONSE_RECEIVED:
      case QUERY_RESPONSE_RECEIVED: {
        if (action.payload.type !== RADAR_CREATE_SESSIONS && action.payload.type !== RADAR_POLL) {
          return state;
        }
        if (
          isFlightsResponseError(action.payload.response) ||
          !isResponseContainFlightsBody(action.payload.response)
        ) {
          return state;
        }

        hasProcessedRadarResponse = true;

        const flightsResponseBody = getFlightsResponseBody(action.payload.response);
        const { carriers, currentResultIds, multiple } = mapCarriers(
          flightsResponseBody.results || [],
          flightsResponseBody.filterStats,
        );
        const byId = Object.fromEntries(carriers.map((carrier) => [carrier.id, carrier]));

        let correctedState = state;
        if (frozenInferDefault === null) {
          frozenInferDefault = hasUserDispatchedFilter
            ? true
            : inferDefaultCarrierValueResolvable(state, byId);
        } else if (frozenInferDefault === true && !hasUserDispatchedFilter) {
          const correctedDefault = inferDefaultCarrierValueResolvable(state, byId);
          if (!correctedDefault) {
            frozenInferDefault = false;
            correctedState = Object.fromEntries(
              Object.entries(state).map(([key, entry]) => {
                if (key === MULTIPLE_KEY && !multipleFollowsInferDefault) return [key, entry];
                return entry.inferred && entry.value
                  ? [key, { value: false, inferred: true }]
                  : [key, entry];
              }),
            );
          }
        }

        const next = reconcileCarriersFilterState({
          state: correctedState,
          byId,
          currentResultIds,
          multiple,
          multipleFollowsInferDefault,
          inferDefault: frozenInferDefault,
        });

        // Unfreeze once polling completes — the carrier universe is settled
        // and user interactions should freely affect inferDefault for the
        // next cycle.
        const radarStatus = action.payload.response.body?.context?.status;
        if (radarStatus === ResultStatus.COMPLETE) {
          frozenInferDefault = null;
        }

        return next;
      }

      default:
        return state;
    }
  };
};

export const get = (state: CarriersFilterState): CarriersFilterState => state;
export default createCarriersReducer();
